/**
 * Contact edit card — the write surface for a contact's relationship
 * context (status, action state, due date, next action, pain point,
 * strategy, notes). Replaces the former read-only "Relationship context"
 * card: without this, the app has no way to set the next-action/due-date
 * pair that puts a contact into the daily queue, which is why every one of
 * the 1,513 contacts has an empty relationship card today.
 *
 * Model distinction: `contact.notes` (edited here) is standing context that
 * gets overwritten in place. `crm_activities` (see ActivityTimeline) is an
 * append-only log of what happened over time — a different model. Saving
 * this form auto-appends a field-diff entry to that log via
 * `useUpdateContact`, but the two are not the same data.
 *
 * This component makes zero network calls on render: it only reads props
 * and writes through `useUpdateContact` / `useMarkDone` (Supabase mutations,
 * free), on user-initiated submit/click. It never imports from `src/lib/ai/`
 * or any `/api/crm/*` route.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { useMarkDone, useUpdateContact } from '@/hooks/crm/useContacts';
import {
  contactEditSchema,
  contactToEditValues,
  editValuesToUpdate,
  type ContactEditValues,
} from '@/lib/crm/contact-edit-schema';
import { resolveActionStatePresentation, resolveStatusPresentation } from '@/lib/crm/labels';
import {
  ACTION_STATE_LABELS,
  STATUS_LABELS,
  type ActionState,
  type Contact,
  type ContactStatus,
} from '@/lib/crm/types';

// The raw, pre-transform shape RHF actually binds native controls to: every
// select/input/textarea always holds a string, so the "clear it" sentinels
// ('' for due_date/text fields, '' for the not-set action_state option)
// live here, not `null`. `contactEditSchema`'s Zod `.transform()`s turn this
// into `ContactEditValues` at submit time (see the cast in `onSubmit`).
interface ContactEditFormInput {
  status: ContactStatus;
  action_state: ActionState | '';
  due_date: string;
  next_action: string;
  pain_point: string;
  strategy: string;
  notes: string;
}

function toFormInput(values: ContactEditValues): ContactEditFormInput {
  return {
    status: values.status,
    action_state: values.action_state ?? '',
    due_date: values.due_date ?? '',
    next_action: values.next_action ?? '',
    pain_point: values.pain_point ?? '',
    strategy: values.strategy ?? '',
    notes: values.notes ?? '',
  };
}

export interface ContactEditCardProps {
  contact: Contact;
}

export function ContactEditCard({ contact }: ContactEditCardProps): JSX.Element {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const updateContact = useUpdateContact();
  const markDone = useMarkDone();

  const form = useForm<ContactEditFormInput>({
    resolver: zodResolver(contactEditSchema),
    defaultValues: toFormInput(contactToEditValues(contact)),
    mode: 'onSubmit',
  });

  const watchedStatus = form.watch('status');
  const watchedActionState = form.watch('action_state');
  const statusPresentation = resolveStatusPresentation(watchedStatus);
  const actionStatePresentation = resolveActionStatePresentation(watchedActionState || null);

  const hasNoRelationshipContext = !contact.due_date
    && !contact.next_action
    && !contact.pain_point
    && !contact.strategy
    && !contact.notes;

  const handleCancel = (): void => {
    setSubmitError(null);
    form.reset(toFormInput(contactToEditValues(contact)));
  };

  const handleMarkDone = (): void => {
    markDone.mutate(contact.id);
  };

  const onSubmit = form.handleSubmit(async (rawValues) => {
    // `zodResolver` (@hookform/resolvers/zod@3.10.0, installed) already ran
    // `contactEditSchema`'s transform at runtime: its implementation passes
    // the parsed `data` through (not the raw input) whenever `raw` isn't
    // set. Its TS types don't model that transformed output, so `rawValues`
    // is really shaped like `ContactEditValues` here even though the static
    // type says `ContactEditFormInput`. This is the single boundary cast
    // for that gap.
    const values = rawValues as unknown as ContactEditValues;
    const updates = editValuesToUpdate(values);
    const previousActionState = contact.action_state ?? null;

    if (values.action_state !== previousActionState) {
      updates.action_state_changed_at = new Date().toISOString();
    }

    setSubmitError(null);

    try {
      await updateContact.mutateAsync({
        id: contact.id,
        updates,
        originalContact: contact,
      });
      form.reset(toFormInput(values));
    } catch {
      setSubmitError('Failed to save changes. Please try again.');
      form.reset(toFormInput(contactToEditValues(contact)));
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Relationship context</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasNoRelationshipContext && (
          <p className="text-sm text-muted-foreground" role="status">
            Set a next action and a due date to put this contact in your daily queue.
          </p>
        )}

        {submitError && (
          <p className="text-sm text-destructive" role="alert">
            {submitError}
          </p>
        )}

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="contact-edit-status">Status</Label>
                {statusPresentation && (
                  <Badge variant="outline" className={statusPresentation.className}>
                    {statusPresentation.label}
                  </Badge>
                )}
              </div>
              <NativeSelect
                id="contact-edit-status"
                className="h-11 md:h-11"
                data-content-origin="domain"
                {...form.register('status')}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="contact-edit-action-state">Action state</Label>
                {actionStatePresentation && (
                  <Badge variant="outline" className={actionStatePresentation.className}>
                    {actionStatePresentation.label}
                  </Badge>
                )}
              </div>
              <NativeSelect
                id="contact-edit-action-state"
                className="h-11 md:h-11"
                data-content-origin="domain"
                {...form.register('action_state')}
              >
                <option value="">Not set</option>
                {Object.entries(ACTION_STATE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-edit-due-date">Due date</Label>
              <Input
                id="contact-edit-due-date"
                type="date"
                className="h-11 md:h-11"
                data-content-origin="domain"
                {...form.register('due_date')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-edit-next-action">Next action</Label>
            <Textarea
              id="contact-edit-next-action"
              rows={2}
              data-content-origin="domain"
              {...form.register('next_action')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-edit-pain-point">Pain point</Label>
            <Textarea
              id="contact-edit-pain-point"
              rows={2}
              data-content-origin="domain"
              {...form.register('pain_point')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-edit-strategy">Strategy</Label>
            <Textarea
              id="contact-edit-strategy"
              rows={2}
              data-content-origin="domain"
              {...form.register('strategy')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-edit-notes">Notes</Label>
            <Textarea
              id="contact-edit-notes"
              rows={3}
              data-content-origin="user"
              {...form.register('notes')}
            />
          </div>

          {form.formState.isDirty && (
            <div
              className="sticky bottom-4 z-10 flex flex-wrap items-center gap-3 rounded-md border border-border bg-card p-4 shadow-elevation-2"
              role="region"
              aria-label="Unsaved changes"
            >
              <Button
                type="submit"
                disabled={updateContact.isPending}
                className="min-h-11 md:min-h-11"
              >
                Save changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={updateContact.isPending}
                className="min-h-11 md:min-h-11"
              >
                Cancel
              </Button>
            </div>
          )}
        </form>

        <Button
          type="button"
          variant="secondary"
          onClick={handleMarkDone}
          disabled={markDone.isPending}
          className="min-h-11 md:min-h-11"
        >
          Mark done
        </Button>
      </CardContent>
    </Card>
  );
}
